/**
 * @format
 * @module lib/topics/api
 */
import { validStatus, handleJSON } from "utils/fetch";
import isNil from "lodash/isNil";
import ee from "lib/eventemitter";
import logger from "utils/logger";
import {
  API_TOPICS_CHECK_BY_ID,
  API_TOPICS_CHECK_BY_SLUG,
  API_TOPICS_FOLLOW,
  API_TOPICS_UNFOLLOW,
  API_TOPICS_AND_ARTICLES,
  API_TOPICS_ALL_FOLLOWED,
  API_TOPICS_LATEST,
  API_TOPICS_TRENDING,
  API_TOPICS_RECOMMENDED,
  API_TOPICS_SEARCH,
  API_AUTHOR_FOLLOW,
  API_AUTHOR_UNFOLLOW
} from "lib/gamservices";

const debugTag = "[TOPICS-API]";

// For follow/unfollow regular topic
export const TOPIC_IDS = "topicIds";
export const TOPIC_SLUGS = "topicSlugs";

// For follow/unfollow author topic
export const AUTHOR_ID = "id";
export const AUTHOR_SLUG = "slug";

// Common fetch GET configuration
const GET_CONFIG = {
  method: "GET",
  mode: "cors"
};

// Common fetch POST configuration
const postConfig = body => ({
  method: "POST",
  mode: "cors",
  headers: {
    "Content-Type": "application/json"
  },
  body: JSON.stringify(body)
});

/**
 * Unfollows a topic
 * @param {String} hashId - user's hash id
 * @param {String} topicId - unique topic identifier (either id or slug of the topic to delete)
 * @param {String} [idType="topicsIds"] - type of topic identifier (possible values: topicIds or topicSlug)
 * @returns {Promise<Object>} - success or error object
 */
export const unfollowTopic = (hashId, topicId, idType = TOPIC_IDS) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to unfollow topic for: ${hashId}`);
  }
  const postBody = idType === TOPIC_IDS ? { hashId, topicId } : { hashId, topicSlug: topicId };

  return fetch(API_TOPICS_UNFOLLOW, postConfig(postBody))
    .then(validStatus)
    .then(handleJSON)
    .then(data => {
      logger.info(`${debugTag} topic ${topicId} unfollowed successfully.`);
      ee.trigger("topic:unfollowed", [hashId, topicId, data]);
      return data;
    });
};

/**
 * Follows a topic
 * @param {String} hashId - user's hash id
 * @param {String} topicId - unique identifier of the topic to follow (either topic ID or slug)
 * @param {String} [idType="topicsIds"] - type of topic identifier (possible values: topicIds or topicSlug)
 * @returns {Promise<Object>} - success or error object
 */
export const followTopic = (hashId, topicId, idType = TOPIC_IDS) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to follow topic for: ${hashId}`);
  }
  const postBody = idType === TOPIC_IDS ? { hashId, topicId } : { hashId, topicSlug: topicId };

  return fetch(API_TOPICS_FOLLOW, postConfig(postBody))
    .then(validStatus)
    .then(handleJSON)
    .then(data => {
      logger.info(`${debugTag} topic ${topicId} followed successfully.`);
      ee.trigger("topic:followed", [hashId, topicId, data]);
      return data;
    });
};

/**
 * Checks whether (a) topic(s) exists based on topic ID or slug.
 * @param {String} hashId - user's hash id
 * @param {Array<String>} itemsToCheck - topic IDs/slugs array
 * @param {String} checkType - possible values: TOPIC_IDS, "topicSlugs"
 * @param {Boolean} [isAuthor=false] - if topic is an author
 * @returns {Promise<Object>} - success or error object
 */
export const topicsExist = (hashId, itemsToCheck, checkType, isAuthor = false) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to check whether topics exist for: ${hashId}`);
  }
  logger.info(`${debugTag} topicsExist called with ${checkType}:`, itemsToCheck);

  const url = checkType === TOPIC_IDS ? API_TOPICS_CHECK_BY_ID : API_TOPICS_CHECK_BY_SLUG;
  const ids = itemsToCheck.join(",");

  const postBody =
    checkType === TOPIC_IDS
      ? { hashId, topicIds: ids }
      : { hashId, topicSlugs: ids, author: isAuthor };

  return fetch(url, postConfig(postBody)).then(validStatus).then(handleJSON);
};

/**
 * Returns a user's followed topics and latest articles under those topics.
 * @param {String} sortType - how to sort the results (alphabetically or by latest)
 * @param {String} hashId - user's hash id
 * @param {Number} topicLimit - max topics to return
 * @param {Number} articleLimit - max articles to return
 * @param {Number} index - start index of topic to return (for pagination purposes)
 * @param {Number} days - latest number of days of articles to return
 * @returns {Promise<Object>} - success or error object
 */
export const getTopicsAndArticles = (
  sortType = "alpha",
  hashId,
  topicLimit = 50,
  articleLimit = 4,
  index = 0,
  days = 7
) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to get topics and articles for: ${hashId}`);
  }
  const endpoint = sortType === "alpha" ? API_TOPICS_AND_ARTICLES : API_TOPICS_LATEST;
  const url = `${endpoint}?hashId=${hashId}&limit=${topicLimit}&articleLimit=${articleLimit}&index=${index}&days=${days}`;
  logger.info(`${debugTag} getTopicsAndArticles called:`, url);

  return fetch(url, GET_CONFIG)
    .then(validStatus)
    .then(handleJSON)
    .then(response =>
      sortType === "alpha" ? normalizeTopics(response.topics) : normalizeTopics(response.articles)
    );
};

/**
 * Fetch all topics that the given user is following
 * @param {String} hashId - user's hash id
 * @returns {Promise<Object>} - success or error object
 */
export const getAllTopicsFollowed = hashId => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to fetch all topics for: ${hashId}`);
  }
  const url = `${API_TOPICS_ALL_FOLLOWED}?hashId=${hashId}`;
  logger.info(`${debugTag} getAllTopicsFollowed called:`, url);

  return fetch(url, GET_CONFIG)
    .then(validStatus)
    .then(handleJSON)
    .then(response => normalizeTopics(response));
};

/**
 * Fetch trending topics for a given user
 * @param {String} hashId - user's hash id
 * @param {Number} [timeframe=30] - timeframe used to calculate trending topics
 * @param {Number} [limit=5] - maximum number of topics to return
 * @returns {Promise<Object>} - success or error object
 */
export const getTrendingTopics = (hashId, timeframe = 30, limit = 5) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to fetch trending topics for: ${hashId}`);
  }
  const url = `${API_TOPICS_TRENDING}?hashId=${hashId}&timeframe=${timeframe}&limit=${limit}`;
  logger.info(`${debugTag} getTrendingTopics called:`, url);

  return fetch(url, GET_CONFIG).then(validStatus).then(handleJSON);
};

/**
 * Fetch recommended topics and authors for a given user
 * @param {String} hashId - user's hash id
 * @param {Number} [limit=5] - maximum number of topics to return
 * @returns {Promise<Object>} - success or error object
 */
export const getRecommendedTopics = (hashId, limit = 5) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to fetch recommended topics for: ${hashId}`);
  }
  const url = `${API_TOPICS_RECOMMENDED}?followItemType=author%2Ctopic&hashId=${hashId}&limit=${limit}`;
  logger.info(`${debugTag} getRecommendedTopics called:`, url);

  return fetch(url, GET_CONFIG).then(validStatus).then(handleJSON);
};

/**
 * Search for topics
 * @param {Object} c - configuration
 * @param {String} c.query - text to search for (required)
 * @param {String} [c.type=all] - type of topics to search for "all", "topics" or "authors"
 * @param {Number} [c.limitAuthors=2] - maximum number of authors to return
 * @param {Number} [c.limitTopics=5] - maximum number of topics to return
 * @param {Number} [c.threshold=0.2] - can range from 0 (very strict search) to 1 (less strict)
 * @param {AbortSignal} [c.signal=null] - AbortSignal to cancel the fetch request
 * @returns {Promise}
 */
export const searchTopics = ({
  query,
  type = "all",
  limitAuthors = 2,
  limitTopics = 5,
  minMatchCharLength = 3,
  threshold = 0.1,
  signal = null
}) => {
  const url = `${API_TOPICS_SEARCH}?query=${query}&type=${type}&limitAuthors=${limitAuthors}&limitTopics=${limitTopics}&minMatchCharLength=${minMatchCharLength}&threshold=${threshold}`;
  const config = signal ? { ...GET_CONFIG, signal } : GET_CONFIG;
  logger.info(`${debugTag} searchTopics called:`, url, config);

  return fetch(url, config).then(validStatus).then(handleJSON);
};

/**
 * Follows an author
 * @param {String} hashId - user's hash id
 * @param {String} topicId - unique identifier of the topic to follow (either topic ID or slug)
 * @param {String} [idType="id"] - type of topic identifier (possible values: id or slug)
 * @returns {Promise<Object>} - success or error object
 */
export const followAuthor = (hashId, topicId, idType = AUTHOR_ID) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to follow author for: ${hashId}`);
  }
  const postBody = idType === AUTHOR_ID ? { hashId, topicId } : { hashId, topicSlug: topicId };

  return fetch(API_AUTHOR_FOLLOW, postConfig(postBody))
    .then(validStatus)
    .then(handleJSON)
    .then(data => {
      logger.info(`${debugTag} author ${topicId} followed successfully.`);
      ee.trigger("author:followed", [hashId, topicId, data]);
      return data;
    });
};

/**
 * Unfollows an author
 * @param {String} hashId - user's hash id
 * @param {String} topicId - unique identifier of the topic to follow (either topic ID or slug)
 * @param {String} [idType="id"] - type of topic identifier (possible values: id or slug)
 * @returns {Promise<Object>} - success or error object
 */
export const unfollowAuthor = (hashId, topicId, idType = AUTHOR_ID) => {
  if (isNil(hashId)) {
    return Promise.reject(`Unable to unfollow author for: ${hashId}`);
  }
  const postBody = idType === AUTHOR_ID ? { hashId, topicId } : { hashId, topicSlug: topicId };

  return fetch(API_AUTHOR_UNFOLLOW, postConfig(postBody))
    .then(validStatus)
    .then(handleJSON)
    .then(data => {
      logger.info(`${debugTag} author ${topicId} unfollowed successfully.`);
      ee.trigger("author:unfollowed", [hashId, topicId, data]);
      return data;
    });
};

/**
 * Normalizes topics because some of the APIs return topics with "slug" and "name"
 * properties, while others have "topicSlug" and "topicName" properties
 * @param {Array<Topic>} [items=[]] - of topics
 * @returns {Array<Topic>}
 */
export function normalizeTopics(items = []) {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map(t => {
    if (t.name && !t.topicName) {
      t.topicName = t.name;
      // TODO: make sure this property is no longer being used, so we can delete it
      // delete t.name;
    }
    if (t.slug && !t.topicSlug) {
      t.topicSlug = t.slug;
      // TODO: make sure this property is no longer being used, so we can delete it
      // delete t.slug;
    }
    return t;
  });
}
